import { Select, Tooltip } from 'antd';

import { createConditionKey, createConditionOptionValue } from './userFormUtils';

const { Option } = Select;

type StringOrNumberOptional = string | number | undefined;
type StringOrNumberPropertyNames<T> = {
  [K in keyof T]: T[K] extends StringOrNumberOptional ? K : never;
}[keyof T];

export function generateOptions<T extends { name: StringOrNumberOptional; id: StringOrNumberOptional }>(
  options: T[],
  labelKey?: StringOrNumberPropertyNames<T>,
  valueKey?: StringOrNumberPropertyNames<T>,
  alternativeLabelKey?: StringOrNumberPropertyNames<T>,
  search?: string
): JSX.Element[];
export function generateOptions<T extends { id: StringOrNumberOptional }>(
  options: T[],
  labelKey: StringOrNumberPropertyNames<T>,
  valueKey?: StringOrNumberPropertyNames<T>,
  alternativeLabelKey?: StringOrNumberPropertyNames<T>,
  search?: string
): JSX.Element[];
export function generateOptions<T extends Record<string, unknown>>(
  options: T[],
  labelKey: StringOrNumberPropertyNames<T>,
  valueKey: StringOrNumberPropertyNames<T>,
  alternativeLabelKey?: StringOrNumberPropertyNames<T>,
  search?: string
): JSX.Element[];
export function generateOptions<T extends Record<string, any>>(
  options: T[],
  labelKey: StringOrNumberPropertyNames<T> = 'name' as any,
  valueKey: StringOrNumberPropertyNames<T> = 'id' as any,
  alternativeLabelKey?: StringOrNumberPropertyNames<T>,
  search?: string
): JSX.Element[] {
  return options.map((option) => {
    const valueOpt = option[valueKey];
    const labelOpt = option[labelKey];
    const alternativeLabel =
      alternativeLabelKey && option[alternativeLabelKey] ? ` (${option[alternativeLabelKey]})` : '';

    const searchForSelect = search ? search : String(valueOpt) + String(labelOpt) + String(alternativeLabel);

    return (
      <Option key={valueOpt} value={valueOpt} search={searchForSelect} disabled={option.disabled}>
        {`${labelOpt}${alternativeLabel}`}
      </Option>
    );
  });
}

export const generateUserOptions = (users: { id: number; firstName: string; lastName: string }[]): JSX.Element[] => {
  return users.map((user) => {
    const { id, firstName, lastName } = user;
    const display = `${firstName} ${lastName}`;

    return (
      <Option key={id} value={id} data-searchvalue={display + id.toString()}>
        {display}
      </Option>
    );
  });
};

export const generateRoleOptions = (
  roles: { id: number; name: string; label: string }[],
  userRoles?: { roleId: number; condition: any }[]
): JSX.Element[] => {
  const options: JSX.Element[] = [];

  roles.forEach((role) => {
    const { id, name, label } = role;
    const baseDisplay = label ? `${name}_${label}` : name;

    // Find ALL instances of this role for the current user
    const userRolesForThisRole = userRoles?.filter((ur) => ur.roleId === id) ?? [];

    // Always show the basic role option (no_condition)
    const basicRoleOptionValue = createConditionOptionValue(id, null);
    options.push(
      <Option key={basicRoleOptionValue} value={basicRoleOptionValue} data-searchvalue={baseDisplay + id.toString()}>
        {baseDisplay}
      </Option>
    );

    // If user has this role with conditions, also show those options
    if (userRolesForThisRole.length > 0) {
      userRolesForThisRole.forEach((userRole) => {
        const conditionKey = createConditionKey(userRole.condition);
        const hasCondition = conditionKey !== 'no_condition';

        if (!hasCondition) {
          return;
        }

        const conditionalRoleOptionValue = createConditionOptionValue(id, userRole.condition);

        const displayText = baseDisplay;

        const tooltipContent = (
          <div>
            <strong>Role:</strong> {baseDisplay}
            <br />
            <strong>Condition:</strong>
            <pre style={{ margin: '4px 0', fontSize: '12px', color: 'white' }}>{conditionKey}</pre>
          </div>
        );

        options.push(
          <Option
            key={conditionalRoleOptionValue}
            value={conditionalRoleOptionValue}
            data-searchvalue={displayText + id.toString()}
          >
            <Tooltip title={tooltipContent} placement="top">
              <span style={{ cursor: 'help' }}>
                {displayText}
                <span style={{ color: '#1890ff', marginLeft: '4px' }}>⚙️</span>
              </span>
            </Tooltip>
          </Option>
        );
      });
    }
  });

  return options;
};

export const generateOptionsFromObject = (options: Record<string, string>): JSX.Element[] => {
  const transformedOptions = Object.entries(options).map(([key, value]) => {
    return {
      id: value,
      name: key
    };
  });

  return generateOptions(transformedOptions);
};
